/**
 * Environment variable utility that supports both runtime injection (via window.__ENV__)
 * and build-time variables (via import.meta.env).
 *
 * Priority order:
 * 1. window.__ENV__[key] (runtime injected in Docker/production)
 * 2. import.meta.env[key] (build-time or dev-time via Vite)
 * 3. fallback (hardcoded default)
 *
 * This allows Coolify to set environment variables at runtime without rebuilding,
 * while maintaining compatibility with the Vite dev workflow.
 */

/**
 * Get an environment variable with fallback support
 * @param key - The environment variable key (e.g., "VITE_API_URL")
 * @param fallback - Default value if neither runtime nor build-time var is found
 * @returns The environment variable value or fallback
 */
export function getEnvVar(key: string, fallback: string): string {
  // Check runtime injected variables first (from Docker container env vars)
  if (typeof window !== "undefined" && window.__ENV__?.[key]) {
    return window.__ENV__[key];
  }

  // Fall back to build-time variables (from Vite)
  if (import.meta.env[key]) {
    return import.meta.env[key] as string;
  }

  // Use hardcoded fallback
  return fallback;
}

/**
 * Wait for an environment variable to become available, with timeout support.
 * In dev mode (where window.__ENV__ doesn't exist), this returns immediately.
 * In production, waits up to the specified timeout for window.__ENV__ to appear.
 *
 * @param key - The environment variable key (e.g., "VITE_API_URL")
 * @param fallback - Default value if neither runtime nor build-time var is found
 * @param timeoutMs - Maximum time to wait for window.__ENV__ to appear (default: 100ms)
 * @returns Promise that resolves to the environment variable value or fallback
 */
export async function waitForEnvVar(
  key: string,
  fallback: string,
  timeoutMs: number = 500,
): Promise<string> {
  // In dev mode, window.__ENV__ doesn't exist, so check immediately and return
  if (typeof window === "undefined") {
    return getEnvVar(key, fallback);
  }

  // If window.__ENV__ already exists and has the key, return immediately
  if (window.__ENV__?.[key]) {
    return window.__ENV__[key];
  }

  // If import.meta.env has the value, return immediately (dev mode)
  if (import.meta.env[key]) {
    return import.meta.env[key] as string;
  }

  // In production, wait for window.__ENV__ to appear (it's injected by the entrypoint script)
  // Check if window.__ENV__ exists but doesn't have our key yet
  const startTime = Date.now();
  const checkInterval = 25; // Check every 25ms

  return new Promise((resolve) => {
    const check = () => {
      // Check if window.__ENV__ now has our key
      if (window.__ENV__?.[key]) {
        resolve(window.__ENV__[key]);
        return;
      }

      // Check if timeout has been reached
      if (Date.now() - startTime >= timeoutMs) {
        // Timeout reached, use fallback logic
        resolve(getEnvVar(key, fallback));
        return;
      }

      // Continue checking
      setTimeout(check, checkInterval);
    };

    check();
  });
}
